/**
 * The Company brand an app receives in its theme (`FiasTheme.brand`).
 *
 * Only on a Company portal (ADR-024): the brand is the portal's, already
 * public there (the unauthenticated host profile draws the portal's shell and
 * sign-in with it), so handing it to the apps the portal runs discloses
 * nothing new. On fias.io there is no brand to give — a workspace member who
 * is not a Company member cannot read it today — so apps keep their own
 * palette.
 *
 * It rides the theme, which is init-payload transport (capability placement,
 * transport exception 1), not a new op, entity or permission.
 */
import { useMemo } from 'react';
import { normalizeFiasBrand, type FiasBrand, type FiasTheme } from '@fias/arche-sdk';
import type { CompanyPortalBrand } from '@/lib/auth-api';
import { usePortalProfile } from '@/auth/company/usePortalProfile';

/** The portal's brand as apps receive it, or undefined when it has none. */
export function pluginBrandFromPortal(
  brand: CompanyPortalBrand | null | undefined,
): FiasBrand | undefined {
  if (!brand) return undefined;
  return normalizeFiasBrand({
    displayName: brand.displayName,
    logoUrl: brand.logoDataUrl,
    primaryColor: brand.primaryColor,
    accentColor: brand.accentColor,
    headerTextColor: brand.headerTextColor,
  });
}

/**
 * The brand of the portal this page is on, for the apps it runs; undefined on
 * fias.io (the profile query is disabled there) and on a portal without one.
 * Stable across renders while the profile is unchanged.
 */
export function usePortalPluginBrand(): FiasBrand | undefined {
  const { profile } = usePortalProfile();
  return useMemo(() => pluginBrandFromPortal(profile?.brand), [profile?.brand]);
}

/**
 * `theme` carrying the host-resolved `brand` and no other: any brand already
 * on `theme` is dropped. A builder `customTheme` is contributor-saved JSON, so
 * a `brand` in it would otherwise reach the app on fias.io looking exactly like
 * a portal's. Returns `theme` itself when there is nothing to change.
 */
export function withPluginBrand(theme: FiasTheme, brand: FiasBrand | undefined): FiasTheme {
  if (brand) return { ...theme, brand };
  if (!('brand' in theme)) return theme;
  const { brand: _ignored, ...unbranded } = theme;
  return unbranded;
}
